import { HardDrive } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { OrgStorageSummary } from '../../types/dashboard'
import { ExpandableTile, StatRow } from '../../components/dashboard'
import { formatBytes, progressBarColour } from './helpers'

const PERCENT_MAX = 100
const TOP_CASES_LIMIT = 5
const TRUNCATE_PX = 140

function StoragePercentBar({ percent }: { percent: number }): React.ReactElement {
  const { t } = useTranslation('admin')
  const colour = progressBarColour(percent)
  const width = Math.min(percent, PERCENT_MAX)
  return (
    <div className="mt-2">
      <div className="mb-1 flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
        <span>{t('stats.usage')}</span>
        <span>{String(Math.round(percent))}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full" style={{ background: 'var(--highlight)' }}>
        <div className={`h-full transition-all ${colour}`} style={{ width: `${String(width)}%` }} />
      </div>
    </div>
  )
}

function UsageBlock({ summary }: { summary: OrgStorageSummary | null }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-1">
      <p
        className="label-mono text-xs font-medium tracking-wider uppercase"
        style={{ color: 'var(--text-muted)' }}
      >
        {t('stats.usageSummary')}
      </p>
      <StatRow label={t('stats.used')} value={formatBytes(summary?.usedBytes ?? 0)} highlight />
      <StatRow
        label={t('stats.quota')}
        value={summary?.quotaBytes ? formatBytes(summary.quotaBytes) : t('stats.unlimited')}
      />
      {summary?.quotaBytes && <StoragePercentBar percent={summary.percentUsed} />}
    </div>
  )
}

function TopCasesBlock({
  summary,
}: {
  summary: OrgStorageSummary | null
}): React.ReactElement | null {
  const { t } = useTranslation('admin')
  if (!summary?.cases || summary.cases.length === 0) return null
  return (
    <div className="space-y-1 border-t pt-2" style={{ borderColor: 'var(--border)' }}>
      <p
        className="label-mono text-xs font-medium tracking-wider uppercase"
        style={{ color: 'var(--text-muted)' }}
      >
        {t('stats.topCasesByStorage')}
      </p>
      {summary.cases.slice(0, TOP_CASES_LIMIT).map((caseItem) => (
        <div key={caseItem.id} className="flex items-center justify-between py-1">
          <span
            className="truncate text-sm"
            style={{ color: 'var(--text-dim)', maxWidth: `${String(TRUNCATE_PX)}px` }}
          >
            {caseItem.name}
          </span>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {formatBytes(caseItem.dataVolumeBytes)}
          </span>
        </div>
      ))}
    </div>
  )
}

export function StorageTile({
  summary,
}: {
  summary: OrgStorageSummary | null
}): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <ExpandableTile
      title={t('stats.dataStorage')}
      value={formatBytes(summary?.usedBytes ?? 0)}
      icon={HardDrive}
    >
      <div className="space-y-3">
        <UsageBlock summary={summary} />
        <TopCasesBlock summary={summary} />
      </div>
    </ExpandableTile>
  )
}
